import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, Container, Row, Table, Form, Badge } from "react-bootstrap";
import {
  DatatableWrapper, Filter, Pagination,
  PaginationOptions, TableBody, TableHeader,
} from "react-bs-datatable";
import InfoPill from "../InfoPill";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import SparkApi from "../../api/SparkApi";
import { toast, ToastContainer } from "react-toastify";

const ExportData = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    const load = async () => {
      const result = await SparkApi.getProducts();
      if (result?.records) setProducts(result.records);
      else setProducts([]);
    };
    load();
  }, []);

  const allSelected = products.length > 0 && selected.length === products.length;

  const toggleOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelected(allSelected ? [] : products.map((p) => p.id));

  const header = useMemo(
    () => [
      {
        title: "",
        prop: "select",
        cell: (row) => (
          <Form.Check
            type="checkbox"
            checked={selected.includes(row.id)}
            onChange={() => toggleOne(row.id)}
          />
        ),
      },
      { title: "Product Name", prop: "name",         isFilterable: true, isSortable: true },
      { title: "Company",      prop: "company_name", isFilterable: true, isSortable: true },
      { title: "Barcode",      prop: "barcode",      isFilterable: true },
      { title: "Quantity",     prop: "quantity",     isSortable: true },
      { title: "Status",       prop: "status",       isFilterable: true },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  // ── Excel export ──────────────────────────────────────────────
  const fetchImageAsBase64 = async (url) => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () =>
          resolve({ base64: reader.result.split(",")[1], mimeType: blob.type || "image/jpeg" });
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  const mimeToExt = (mime) =>
    mime.includes("png") ? "png" : mime.includes("gif") ? "gif" : "jpeg";

  const handleExport = async () => {
    if (!selected.length) { toast.error("Please select at least one product"); return; }
    setLoading(true);
    try {
      const toExport = products.filter((p) => selected.includes(p.id));

      // ── Pass 1: fetch all files for every selected product ──────
      const fileMap = {};  // productId → files[]
      for (const p of toExport) {
        let files = await SparkApi.fetchFiles(p.id);
        if (!files || !files.length) {
          files = p.fileid ? [{ id: p.fileid, title: p.title, parentid: p.id }] : [];
        }
        fileMap[p.id] = files;
      }

      // Always 3 photo columns — fill what's available, leave rest empty
      const maxPhotos = 3;

      // ── Build workbook ──────────────────────────────────────────
      const wb = new ExcelJS.Workbook();
      wb.creator = "SRM Marketing LLP";
      const ws  = wb.addWorksheet("Products");

      // Fixed cols: Name(A) Company(B) Qty(C) + maxPhotos photo cols + Market(last-1) SRM(last)
      const totalCols    = 3 + maxPhotos + 2;
      const lastColLetter = String.fromCharCode(64 + totalCols); // A=65

      ws.columns = [
        { key: "name",    width: 35 },
        { key: "company", width: 25 },
        { key: "qty",     width: 12 },
        ...Array.from({ length: maxPhotos }, (_, i) => ({ key: `photo${i+1}`, width: 20 })),
        { key: "market",  width: 15 },
        { key: "srm",     width: 15 },
      ];

      // Title
      ws.mergeCells(`A1:${lastColLetter}1`);
      const titleCell     = ws.getCell("A1");
      titleCell.value     = "SRM PRODUCT LIST";
      titleCell.font      = { name: "Arial", size: 20, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FF3D7EBE" } };
      ws.getRow(1).height = 50;

      // Headers — merge photo columns under "PRODUCT PHOTO"
      const photoStartLetter = String.fromCharCode(64 + 4);              // D
      const photoEndLetter   = String.fromCharCode(64 + 3 + maxPhotos);  // D+maxPhotos-1
      const headerValues     = [
        "PRODUCT NAME", "COMPANY NAME", "QUANTITY",
        "PRODUCT PHOTO", ...Array(maxPhotos - 1).fill(""),
        "MARKET PRICE", "SRM PRICE",
      ];
      const hRow = ws.addRow(headerValues);
      if (maxPhotos > 1) ws.mergeCells(`${photoStartLetter}2:${photoEndLetter}2`);
      hRow.height = 35;
      hRow.eachCell((c) => {
        c.font      = { name: "Arial", size: 11, bold: true };
        c.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9E1F2" } };
        c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        c.border    = { top:{style:"medium"}, bottom:{style:"medium"}, left:{style:"medium"}, right:{style:"medium"} };
      });

      // ── Pass 2: write data rows + images ───────────────────────
      for (let i = 0; i < toExport.length; i++) {
        const p      = toExport[i];
        const files  = fileMap[p.id] || [];
        const rowIdx = i + 2; // 0-indexed: title=0, header=1, data starts at 2

        const rowData = [p.name || "", p.company_name || "", p.quantity ?? 0,
          ...Array(maxPhotos).fill(""), "", ""];
        const row = ws.addRow(rowData);
        row.height = 90;
        row.eachCell({ includeEmpty: true }, (c) => {
          c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          c.font      = { name: "Arial", size: 10 };
          c.border    = { top:{style:"thin"}, bottom:{style:"thin"}, left:{style:"thin"}, right:{style:"thin"} };
        });

        // Place each image in its own column (D=col3, E=col4, F=col5, G=col6)
        for (let j = 0; j < Math.min(files.length, maxPhotos); j++) {
          const file    = files[j];
          const ext     = file.title?.split(".").pop() || "jpg";
          const imgData = await fetchImageAsBase64(`/ibs_ibirds/${file.parentid}/${file.id}.${ext}`);
          if (!imgData) continue;

          const imageId  = wb.addImage({ base64: imgData.base64, extension: mimeToExt(imgData.mimeType) });
          const colStart = 3 + j;        // col D=3, E=4, F=5, G=6 (0-indexed)
          const colEnd   = colStart + 1; // always span exactly one column

          ws.addImage(imageId, {
            tl: { col: colStart, row: rowIdx     },
            br: { col: colEnd,   row: rowIdx + 1 },
          });
        }
      }

      const buf = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `SRM_Product_List_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      toast.success(`Exported ${toExport.length} product(s)!`);
    } catch (err) {
      console.error(err);
      toast.error("Export failed. Please try again.");
    }
    setLoading(false);
  };

  // Selected product names for the summary strip
  const selectedProducts = products.filter((p) => selected.includes(p.id));

  return (
    <Container>
      <ToastContainer />
      <Row className="g-0">
        <Col lg={12}>
          <DatatableWrapper
            body={products}
            headers={header}
            paginationOptionsProps={{
              initialState: { rowsPerPage: 10, options: [5, 10, 15, 20] },
            }}
            sortProps={{ initialState: { prop: "name", order: "asc" } }}
          >
            <Row className="mb-4">
              <Col xs={12} lg={4} sm={4} md={7} className="d-flex flex-col justify-content-end align-items-end">
                <Filter placeholder="Search products..." />
              </Col>
              <Col xs={12} lg={4} sm={6} md={5} className="products-Pagination d-flex flex-col align-items-start">
                <PaginationOptions labels={{ beforeSelect: " " }} />
                <div className="mt-2">
                  <InfoPill left="Total Products" right={products.length || 0} />
                </div>
              </Col>
              <Col xs={12} sm={2} lg={4} md={12} className="mt-4 products-btn flex-col align-items-center gap-1">
                <Button
                  className="btn-sm custom-btn-css-add-new-record"
                  variant="outline-primary"
                  onClick={toggleAll}
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </Button>
                <Button
                  className="btn-sm custom-btn-css-add-new-record"
                  variant="outline-primary"
                  onClick={handleExport}
                  disabled={loading || !selected.length}
                >
                  {loading ? "Exporting..." : `Export${selected.length ? ` (${selected.length})` : ""}`}
                </Button>
              </Col>
            </Row>

            <Table striped className="productTable" responsive>
              <TableHeader />
              <TableBody />
            </Table>

            {/* Selected products summary — sits between table and pagination */}
            {selectedProducts.length > 0 && (
              <div
                style={{
                  background: "#f0f7ff",
                  border: "1px solid #c8dff7",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  margin: "10px 0",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span style={{ fontWeight: 600, color: "#3d7ebe", marginRight: 4, fontSize: "0.88rem" }}>
                  Selected ({selectedProducts.length}):
                </span>
                {selectedProducts.map((p) => (
                  <Badge
                    key={p.id}
                    style={{
                      background: "#3d7ebe",
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      padding: "4px 8px",
                    }}
                    onClick={() => toggleOne(p.id)}
                    title="Click to deselect"
                  >
                    {p.name} &times;
                  </Badge>
                ))}
              </div>
            )}

            <Pagination />
          </DatatableWrapper>
        </Col>
      </Row>
    </Container>
  );
};

export default ExportData;